import type { NavigationCategory, NavigationItem } from '../../../../types/navigation'
import { MAX_THIRD_LEVEL_ITEMS, MX_SECTION_MAX_COLUMNS, NUM_COLUMNS } from '../../../../constants/config'

/**
 * Mutable state used while distributing navigation items across columns.
 */
interface ColumnLayoutState {
  columns: NavigationItem[][]
  itemCounts: number[]
  currentColumn: number
  isPromoPlaced: boolean
  /** Number of columns the MX section has consumed so far (undefined until an MX item is seen). */
  mxColumnsConsumed: number | undefined
  /** Index of the first column belonging to the MX section. */
  mxStartColumn: number | undefined
}

/** Validates that a visual item has the necessary properties to be rendered as a promo tile. */
const isValidVisualItem = (item: NavigationItem): boolean => Boolean(item.url && item.image?.url)

/**
 * Distributes navigation items into a dynamic column grid layout.
 * Handles regular text categories, MX merchandising sections, and promotional visual tiles.
 */
export function layoutNavColumns(category: NavigationCategory): NavigationItem[][] {
  const items = category.children ?? []

  if (category.isVisual) {
    return assignItemPositions(layoutVisualCategory(items), true)
  }

  const state = createInitialLayoutState()
  const categoryHasMxSection = items.some((item) => item.startOfMx)

  for (const [index, item] of items.entries()) {
    if (item.isVisual) {
      tryPlacePromoTile(state, item)
      continue
    }

    const wasPlaced = tryPlaceRegularItem(state, item, categoryHasMxSection)
    if (!wasPlaced) break

    if (!categoryHasMxSection) {
      const remainingItems = items.length - (index + 1)
      maybeSpreadToNextColumn(state, remainingItems)
    }
  }

  return assignItemPositions(
    state.columns.filter((column) => column.length > 0),
    false,
  )
}

/**
 * Assigns a 1-based `itemPosition` to every text link that will render in the
 * flyout, walking columns left→right, top→bottom. Also flags every item that
 * belongs to the MX section via `inMx` (set on the `startOfMx` item, every
 * subsequent item in render order, and their rendered children). Mutates the
 * items in place and returns the same columns.
 *
 * Position numbering, in order:
 *   - each subcategory header (only if it has a `url` — otherwise it renders
 *     as a non-link heading)
 *   - each child link of a subcategory (up to MAX_THIRD_LEVEL_ITEMS)
 *
 * Visual items (promo tiles inside a regular category) get `itemPosition = 0`
 * so analytics can still distinguish them from items that were never placed.
 * When the whole category is visual, every visual child is numbered 1..N like
 * a regular link sequence.
 */
function assignItemPositions(columns: NavigationItem[][], categoryIsVisual: boolean): NavigationItem[][] {
  let position = 0
  let inMxSection = false
  for (const column of columns) {
    for (const item of column) {
      if (item.startOfMx) inMxSection = true
      item.inMx = inMxSection

      if (item.isVisual) {
        item.itemPosition = categoryIsVisual ? ++position : 0
        continue
      }
      if (item.url) {
        item.itemPosition = ++position
      }
      const renderedChildren = (item.children ?? []).slice(0, MAX_THIRD_LEVEL_ITEMS)
      for (const child of renderedChildren) {
        child.itemPosition = ++position
        child.inMx = inMxSection
      }
    }
  }
  return columns
}

/**
 * Visual categories render each valid child as its own column.
 */
function layoutVisualCategory(items: NavigationItem[]): NavigationItem[][] {
  return items.filter(isValidVisualItem).map((item) => [item])
}

/** Initializes the mutable state used for column layout. */
function createInitialLayoutState(): ColumnLayoutState {
  return {
    columns: Array.from({ length: NUM_COLUMNS }, () => []),
    itemCounts: new Array<number>(NUM_COLUMNS).fill(0),
    currentColumn: 0,
    isPromoPlaced: false,
    mxColumnsConsumed: undefined,
    mxStartColumn: undefined,
  }
}

/**
 * Places at most one promo tile per flyout. The tile is anchored to the right
 * edge of the MX section (when present) or to the last column otherwise, and
 * any columns to its right are cleared.
 */
function tryPlacePromoTile(state: ColumnLayoutState, promo: NavigationItem): void {
  if (state.isPromoPlaced || !isValidVisualItem(promo)) return

  const promoColumn = state.mxStartColumn != null ? state.mxStartColumn + (MX_SECTION_MAX_COLUMNS - 1) : NUM_COLUMNS - 1

  state.itemCounts[promoColumn] = MAX_THIRD_LEVEL_ITEMS
  state.columns[promoColumn] = [promo]
  for (let columnAfterPromo = promoColumn + 1; columnAfterPromo < NUM_COLUMNS; columnAfterPromo++) {
    state.columns[columnAfterPromo] = []
  }
  state.isPromoPlaced = true
}

/**
 * Places a regular (non-visual) navigation item into the layout, advancing
 * columns when needed. Returns false when layout capacity has been exhausted.
 */
function tryPlaceRegularItem(state: ColumnLayoutState, item: NavigationItem, categoryHasMxSection: boolean): boolean {
  const childCount = Math.min(item.children?.length ?? 0, MAX_THIRD_LEVEL_ITEMS)

  beginMxSectionIfNeeded(state, item)

  const mustForceNextColumn = Boolean((item.pushToNextColumn && categoryHasMxSection) || item.startOfMx)
  advancePastUnavailableColumns(state, childCount, mustForceNextColumn)

  if (isLayoutCapacityExhausted(state)) return false

  state.itemCounts[state.currentColumn]! += childCount
  state.columns[state.currentColumn]?.push(item)
  return true
}

/**
 * If the incoming item is the start of an MX section, mark the beginning of the section in the state so that
 * subsequent items know to reserve columns for it and to place the promo tile correctly.
 */
function beginMxSectionIfNeeded(state: ColumnLayoutState, item: NavigationItem): void {
  if (item.startOfMx && state.mxColumnsConsumed == null) {
    state.mxColumnsConsumed = 0
    state.mxStartColumn = state.currentColumn + 1
  }
}

/**
 * Advances the cursor past columns that are either full for the incoming item
 * or that the item explicitly wants to skip (forced advance).
 */
function advancePastUnavailableColumns(state: ColumnLayoutState, childCount: number, forceAdvanceOnce: boolean): void {
  let forceAdvance = forceAdvanceOnce

  while (state.currentColumn < NUM_COLUMNS && (forceAdvance || isCurrentColumnFull(state, childCount))) {
    advanceCursor(state)
    forceAdvance = false
  }
}

function isCurrentColumnFull(state: ColumnLayoutState, childCount: number): boolean {
  return state.itemCounts[state.currentColumn]! + childCount > MAX_THIRD_LEVEL_ITEMS
}

function advanceCursor(state: ColumnLayoutState): void {
  if (state.mxColumnsConsumed != null) state.mxColumnsConsumed++
  state.currentColumn++
}

/** Checks if we've run out of columns or exceeded the MX section's column budget. */
function isLayoutCapacityExhausted(state: ColumnLayoutState): boolean {
  const isOutOfColumns = state.currentColumn >= NUM_COLUMNS
  const isMxBudgetExceeded = (state.mxColumnsConsumed ?? 0) > MX_SECTION_MAX_COLUMNS
  return isOutOfColumns || isMxBudgetExceeded
}

/**
 * When the remaining items can each occupy their own empty column, move to the
 * next column so items spread out instead of stacking.
 */
function maybeSpreadToNextColumn(state: ColumnLayoutState, remainingItems: number): void {
  const emptyColumnCount = state.itemCounts.filter((count) => count === 0).length
  if (remainingItems <= emptyColumnCount) {
    state.currentColumn++
  }
}
